// ────────────────────────────────────────────────────────────────────────────
// The "⋮" on a table row, and the menu it opens.
//
// Replaces a pencil that was already redundant — the row itself opens the order
// on click, so a dedicated edit button spent a column on the thing the whole
// row already does. A three-dots menu spends that column on everything else an
// order might need, and grows as those actions arrive.
//
// PORTALLED to the document body with fixed positioning. The orders table sits
// in an `overflow-x-auto` wrapper inside a scrolling main, and an absolutely
// positioned panel is clipped by its nearest scrolling ancestor — which is what
// chopped the workflow condition menu until it was portalled too. Fixed
// positioning also lets the panel flip above the button when it sits near the
// bottom of the list, which is where most rows are.
//
// Actions are DATA, not markup: a caller decides what a row in this state can
// do, and `disabledReason` is shown rather than the item being hidden, so the
// menu reads the same shape every time and an operator learns it.
//
// App-level rather than per-feature: orders, order tasks and invoices all put
// their row actions behind the same "⋮", and a second copy would drift.
// ────────────────────────────────────────────────────────────────────────────
import { cn } from '@my/ui';
import { MoreVertical } from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/** Panel width, and the gaps kept from the button and the viewport edges. */
const PANEL_W = 208;
const GAP = 4;
const EDGE = 12;

export interface RowAction {
  /** Stable key — also used for the item's test hook. */
  id: string;
  label: string;
  icon?: ReactNode;
  onSelect: () => void;
  /**
   * Why this action cannot be taken right now. Present = the item renders
   * greyed with the reason as its title, rather than vanishing: an operator
   * asking "why can't I invoice this?" is better served by a disabled row than
   * by an absent one.
   */
  disabledReason?: string;
  /** Destructive actions are separated and coloured. */
  destructive?: boolean;
}

export function RowActionsMenu({
  label,
  actions,
}: {
  /** For the button's accessible name, e.g. the order number. */
  label: string;
  actions: RowAction[];
}) {
  const [open, setOpen] = useState(false);
  const [box, setBox] = useState<{ left: number; top?: number; bottom?: number }>();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  /** Anchor to the button, flipping to whichever side has room. */
  const place = useCallback(() => {
    const r = buttonRef.current?.getBoundingClientRect();
    if (!r) return;
    const above = r.top - EDGE;
    const below = window.innerHeight - r.bottom - EDGE;
    // Rough height: enough to decide which way to open without measuring.
    const wanted = actions.length * 32 + 12;
    const up = below < wanted && above > below;
    setBox({
      // Right-aligned to the button (it lives in a right-hand column), then
      // clamped so it cannot sit off either edge.
      left: Math.min(
        Math.max(r.right - PANEL_W, EDGE),
        Math.max(EDGE, window.innerWidth - PANEL_W - EDGE),
      ),
      ...(up ? { bottom: window.innerHeight - r.top + GAP } : { top: r.bottom + GAP }),
    });
  }, [actions.length]);

  // Before paint, so the panel never renders in the wrong place first.
  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return;

    // `true` for the capture phase: the table scrolls in an inner container,
    // and that scroll does not bubble to window.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);

    // Click-away tests BOTH nodes — the panel is portalled, so it is not a
    // descendant of the button.
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!buttonRef.current?.contains(t) && !panelRef.current?.contains(t)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, place]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-label={`Actions for ${label}`}
        aria-haspopup="menu"
        aria-expanded={open}
        // Every handler stops propagation: the row opens the order on click,
        // so without this, reaching for the menu would navigate away from the
        // list instead of opening it.
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        onDoubleClick={(e) => e.stopPropagation()}
        className={cn(
          'inline-flex h-6 w-6 items-center justify-center rounded-sm text-slate-400',
          'hover:bg-slate-100 hover:text-slate-700',
          open && 'bg-slate-100 text-slate-700',
        )}
      >
        <MoreVertical className="h-4 w-4" />
      </button>

      {open &&
        box &&
        createPortal(
          <div
            ref={panelRef}
            role="menu"
            style={{ left: box.left, top: box.top, bottom: box.bottom, width: PANEL_W }}
            className="fixed z-50 overflow-hidden rounded-md border border-slate-200 bg-white py-1 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            {actions.map((action, i) => {
              const disabled = !!action.disabledReason;
              const previous = actions[i - 1];
              return (
                <div key={action.id}>
                  {/* A destructive action gets a rule above it, so it is never
                      the thing under the cursor by accident. */}
                  {action.destructive && previous && !previous.destructive && (
                    <div className="my-1 border-t border-slate-100" />
                  )}
                  <button
                    type="button"
                    role="menuitem"
                    disabled={disabled}
                    title={action.disabledReason}
                    onClick={() => {
                      setOpen(false);
                      action.onSelect();
                    }}
                    className={cn(
                      'flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs',
                      disabled
                        ? 'cursor-not-allowed text-slate-300'
                        : action.destructive
                          ? 'text-red-600 hover:bg-red-50'
                          : 'text-slate-700 hover:bg-slate-50',
                    )}
                  >
                    {action.icon}
                    <span className="flex-1 truncate">{action.label}</span>
                  </button>
                </div>
              );
            })}
          </div>,
          document.body,
        )}
    </>
  );
}
